import { createSocketIOClient, REALTIME_EVENTS } from '@infra/realtime';
import type { RealtimeClient } from '@infra/realtime';
import { environment } from '@shared/config';
import { getAccessToken, decodeJwtPayload } from '@infra/auth';

/**
 * Singleton Socket.IO client for the NestJS core gateway.
 * Created eagerly with autoConnect: false — connect only after auth is available.
 * Mirrors the pattern of coreHttp = createHttpClient({ ... }) in @services/core/http.
 */
export const coreClient: RealtimeClient = createSocketIOClient({
  url: environment.coreWsUrl || environment.apiUrl,
  path: '/socket.io',
  // Keep polling as a fallback: it gets through corporate firewalls that block the
  // WS upgrade. With no REST fallback, the socket is the only path — it must connect.
  transports: ['websocket', 'polling'],
  autoConnect: false,
  // Lazy wrapper (not a bare `getAccessToken` ref): this singleton is created at
  // module-eval time and `@modules/auth` can still be in its TDZ due to a circular
  // import — defer the reference to call-time (connect), by which point auth is ready.
  auth: { tokenFn: () => getAccessToken() },
});

/**
 * Whether the live socket handshook WITHOUT a usable token. Such a connection
 * authenticates anonymously on the server and is never added to the per-user client
 * map, so it can never receive `auth:session-revoked`. Once a refresh mints a fresh
 * token we re-handshake so the server can associate the socket with the user.
 */
let connectedAnonymously = false;

function hasUsableAccessToken(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  const exp = decodeJwtPayload(token)?.exp;
  if (typeof exp !== 'number') return true; // no exp claim → assume usable
  return exp * 1000 > Date.now();
}

export function connectCoreSocket(): void {
  if (coreClient.isConnected()) return;
  connectedAnonymously = !hasUsableAccessToken();
  coreClient.connect().catch(console.error);
}

export function disconnectCoreSocket(): void {
  coreClient.disconnect();
  connectedAnonymously = false;
}

// socket.io reconnects on its own (reconnection:true) and re-runs `tokenFn`, but never
// touches `connectedAnonymously`. Recompute it on EVERY (re)connect so a socket that
// auto-reconnected AFTER the access token lapsed (tab suspended/offline past the 5-min
// buffer) is correctly flagged anonymous — otherwise the next `auth:token-refreshed`
// early-returns in reconnectCoreSocketIfUnauthenticated and the socket never re-associates
// server-side, silently missing `auth:session-revoked`.
coreClient.on(REALTIME_EVENTS.CONNECTED, () => {
  connectedAnonymously = !hasUsableAccessToken();
});

/**
 * Re-handshake ONLY when the live socket connected anonymously (no/expired token)
 * and a usable token now exists. A socket that already carries a valid token is left
 * alone — avoids needless churn on every proactive refresh (~55 min). Fired from the
 * `auth:token-refreshed` event so it covers both the 401-interceptor and the proactive
 * scheduler refresh paths without importing @modules/auth → @services/core (cycle).
 */
export function reconnectCoreSocketIfUnauthenticated(): void {
  if (!connectedAnonymously || !hasUsableAccessToken()) return;
  connectedAnonymously = false;
  coreClient.reconnect();
}

if (typeof window !== 'undefined') {
  window.addEventListener('auth:token-refreshed', reconnectCoreSocketIfUnauthenticated);
}

export function onCoreEvent<T = unknown>(event: string, handler: (payload: T) => void): () => void {
  return coreClient.on(event, handler);
}
